import {Middleware} from '@reduxjs/toolkit'
import {getLogger} from '../../services/logging'
import {submitSearch} from '../slices/searchSubmittedSlice'
import {selectSearchParams} from '../slices/searchParamsSlice'
import {isRegionSearch} from '../../services/utils'
import {formatISODate, toLocalDateTime} from '../../services/dateUtils'
import {selectConfig} from '../slices/configSlice'
import {IbeConfig} from '../../types'

const logger = getLogger('urlSyncMiddleware')

/**
 * Middleware to sync search parameters to the URL.
 * @param storeApi The redux store API
 * @returns
 */
export const urlSyncMiddleware: Middleware = storeApi => next => action => {
  try {
    const result = next(action)

    if (action.type === submitSearch.type) {
      const state = storeApi.getState()
      const searchParams = selectSearchParams(state)
      const config = selectConfig(state) as IbeConfig
      const isRegion = isRegionSearch(config, searchParams.location)
      const url = new URL(window.location.href)

      // clear existing search parameters
      url.search = ''

      const arrivalDate = searchParams.dateRange.start
        ? formatISODate(toLocalDateTime(searchParams.dateRange.start))
        : ''
      const departureDate = searchParams.dateRange.end
        ? formatISODate(toLocalDateTime(searchParams.dateRange.end))
        : ''
      if (isRegion) {
        url.searchParams.set(
          'region',
          searchParams?.location
            ? encodeURIComponent(searchParams?.location)
            : ''
        )
      } else {
        url.searchParams.set(
          'location',
          searchParams?.location
            ? encodeURIComponent(searchParams?.location)
            : ''
        )
      }
      url.searchParams.set('propertyId', searchParams.propertyId || '')
      url.searchParams.set('arrival', arrivalDate)
      url.searchParams.set('departure', departureDate)
      url.searchParams.set('adults', searchParams.guests.toString())

      // set special rates parameters
      if (searchParams.specialRates.promoCode) {
        url.searchParams.set(
          'promoCode',
          searchParams.specialRates.promoCode || ''
        )
      } else if (searchParams.specialRates.corporateCode) {
        url.searchParams.set(
          'corporateCode',
          searchParams.specialRates.corporateCode || ''
        )
      } else if (searchParams.specialRates.hasAAA) {
        url.searchParams.set('hasAAA', 'true')
      } else if (searchParams.specialRates.hasAARP) {
        url.searchParams.set('hasAARP', 'true')
      } else if (searchParams.specialRates.hasGov) {
        url.searchParams.set('hasGov', 'true')
      } else if (searchParams.specialRates.hasMilitary) {
        url.searchParams.set('hasMilitary', 'true')
      }
      const updatedUrl = url.toString()
      history.replaceState(null, '', updatedUrl)
      logger.info({
        message: 'Search parameters synced to URL',
        data: {
          updatedUrl,
          searchParams
        }
      })
    }

    return result
  } catch (e) {
    logger.error({
      message: 'Error in url sync middleware',
      errorData: {
        error: e.message
      }
    })
  }
}
